import axios from "axios";
import React, { useEffect } from "react";
import { useAuth } from "react-oidc-context";

// Create an instance of axios that will be used throughout the app
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

/**
 * This is a React component that must be rendered within your AuthProvider.
 * Its sole purpose is to use the `useAuth` hook to access the auth token
 * and dynamically inject it into the headers of outgoing Axios requests.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export const AxiosInterceptor = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const auth = useAuth();

  useEffect(() => {
    // This is the request interceptor. It runs before any request is sent.
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        // Try to get the access token from different possible locations
        let accessToken = null;

        // First try to get from localStorage (like the reference implementation)
        const cognitoIdToken = localStorage.getItem("cognito_id_token");
        const cognitoAccessToken = localStorage.getItem("cognito_access_token");

        if (cognitoIdToken) {
          accessToken = cognitoIdToken;
        } else if (cognitoAccessToken) {
          accessToken = cognitoAccessToken;
        } else if (auth.user) {
          // Fallback to auth context if localStorage doesn't have tokens
          accessToken = auth.user.access_token;
        }

        // Debug logging to help troubleshoot
        if (process.env.NODE_ENV === "development") {
          console.log("Auth state:", {
            isAuthenticated: auth.isAuthenticated,
            isLoading: auth.isLoading,
            hasUser: !!auth.user,
            userKeys: auth.user ? Object.keys(auth.user) : [],
            accessToken: accessToken ? "Present" : "Missing",
            localStorageTokens: {
              idToken: !!cognitoIdToken,
              accessToken: !!cognitoAccessToken,
            },
          });
        }

        if (accessToken) {
          // If a token exists, add it to the Authorization header
          config.headers.Authorization = `Bearer ${accessToken}`;

          if (process.env.NODE_ENV === "development") {
            console.log("Adding Bearer token to request:", config.url);
          }
        } else {
          if (process.env.NODE_ENV === "development") {
            console.warn("No access token available for request:", config.url);
          }
        }

        // Keep your existing api-key header
        if (process.env.REACT_APP_API_KEY) {
          config.headers["api-key"] = process.env.REACT_APP_API_KEY;
        }

        return config;
      },
      (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
      },
    );

    // This is the response interceptor. It runs after a response is received.
    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle global errors here.
        if (error.response) {
          console.error("API Error Response:", {
            status: error.response.status,
            statusText: error.response.statusText,
            url: error.response.config?.url,
            data: error.response.data,
          });

          // If we get a 401 and haven't already tried to refresh
          if (error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              // Try to refresh the token
              const refreshToken = localStorage.getItem(
                "cognito_refresh_token",
              );
              if (refreshToken) {
                const response = await axios.post("/auth/cognito/refresh", {
                  refreshToken: refreshToken,
                });

                if (response.data.success) {
                  // Store new tokens
                  localStorage.setItem(
                    "cognito_id_token",
                    response.data.tokens.id_token,
                  );
                  localStorage.setItem(
                    "cognito_access_token",
                    response.data.tokens.access_token,
                  );

                  // Retry the original request with new token
                  originalRequest.headers.Authorization = `Bearer ${response.data.tokens.id_token}`;
                  return axiosInstance(originalRequest);
                }
              }
            } catch (refreshError) {
              // If refresh fails, clear tokens and redirect to login
              localStorage.removeItem("cognito_id_token");
              localStorage.removeItem("cognito_access_token");
              localStorage.removeItem("cognito_refresh_token");
              console.warn("401 Unauthorized - redirecting to login");
              window.location.href = "/";
            }
          } else if (error.response.status === 401) {
            console.warn("401 Unauthorized - redirecting to login");
            window.location.href = "/";
          }
        } else {
          console.error("Network Error:", error.message);
        }
        return Promise.reject(error);
      },
    );

    // Cleanup function to remove the interceptors when the component unmounts
    // or when the auth object changes, to prevent memory leaks.
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [auth]); // Rerun the effect if the auth object changes

  return children as React.ReactElement;
};

export default axiosInstance;
